import { isAufmassPackItem, isWerkstattPackItem, type Montageunterlage } from "@/lib/projectOverview";
import {
  isWerkstattStueckliste,
  pickLatestStuecklisten,
  stuecklisteDisplayNumber,
  type Stueckliste,
} from "@/lib/stuecklisten";

export type UnterlagenPreviewKind = "file" | "note" | "address" | "stueckliste" | "ab";
export type UnterlagenPackKind = "montage" | "werkstatt" | "aufmass";

export interface UnterlagenPreviewItem {
  key: string;
  /** Reiter im Unterlagen-Dialog, der dieses Dokument zeigt. */
  tabKey: string;
  kind: UnterlagenPreviewKind;
  title: string;
  subtitle?: string | null;
}

export interface UnterlagenPreviewCounts {
  files: number;
  notes: number;
  addresses: number;
  stuecklisten: number;
  hasAb: boolean;
}

export const UNTERLAGEN_PREVIEW_LIMIT = 5;

function firstLine(value: string | null | undefined): string {
  return String(value || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .find(Boolean) || "";
}

export function unterlagenItemTitle(item: Montageunterlage): string {
  if (item.entry_type === "file") {
    return String(item.title || item.filename || "Datei").trim() || "Datei";
  }
  if (item.entry_type === "note") {
    return String(item.title || firstLine(item.body_text) || "Notiz").trim() || "Notiz";
  }
  if (item.entry_type === "address") {
    return String(item.title || firstLine(item.body_text) || "Adresse").trim() || "Adresse";
  }
  return String(item.title || "Eintrag").trim() || "Eintrag";
}

export function filterUnterlagenForPack(
  items: Montageunterlage[],
  pack: UnterlagenPackKind,
): Montageunterlage[] {
  return items.filter((item) => {
    if (pack === "werkstatt") return isWerkstattPackItem(item);
    if (pack === "aufmass") return isAufmassPackItem(item);
    return !isWerkstattPackItem(item) && !isAufmassPackItem(item);
  });
}

export function filterStuecklistenForPack(
  stuecklisten: Stueckliste[],
  pack: UnterlagenPackKind,
): Stueckliste[] {
  return pickLatestStuecklisten(stuecklisten).filter((item) => {
    if (pack === "aufmass") return false;
    return pack === "werkstatt" ? isWerkstattStueckliste(item) : !isWerkstattStueckliste(item);
  });
}

function linkedMontageunterlageIds(stuecklisten: Stueckliste[]): Set<number> {
  const ids = new Set<number>();
  for (const sl of stuecklisten) {
    if (sl.montageunterlage_id != null) ids.add(sl.montageunterlage_id);
  }
  return ids;
}

export function countUnterlagenPreview(
  items: Montageunterlage[],
  stuecklisten: Stueckliste[],
  pack: UnterlagenPackKind,
  options: { hasAb?: boolean } = {},
): UnterlagenPreviewCounts {
  const packItems = filterUnterlagenForPack(items, pack);
  const packSl = filterStuecklistenForPack(stuecklisten, pack);
  const linkedIds = linkedMontageunterlageIds(stuecklisten);
  return {
    files: packItems.filter(
      (item) => item.entry_type === "file" && !linkedIds.has(item.id),
    ).length,
    notes: packItems.filter((item) => item.entry_type === "note").length,
    addresses: packItems.filter((item) => item.entry_type === "address").length,
    stuecklisten: packSl.length,
    hasAb: pack === "montage" && Boolean(options.hasAb),
  };
}

export function buildUnterlagenPreview(
  items: Montageunterlage[],
  stuecklisten: Stueckliste[],
  pack: UnterlagenPackKind,
  options: { hasAb?: boolean; limit?: number } = {},
): { items: UnterlagenPreviewItem[]; hiddenCount: number; counts: UnterlagenPreviewCounts } {
  const counts = countUnterlagenPreview(items, stuecklisten, pack, options);
  const packItems = filterUnterlagenForPack(items, pack);
  const packSl = filterStuecklistenForPack(stuecklisten, pack)
    .slice()
    .sort((a, b) => {
      const an = stuecklisteDisplayNumber(a);
      const bn = stuecklisteDisplayNumber(b);
      return an.localeCompare(bn, "de");
    });
  const linkedIds = linkedMontageunterlageIds(stuecklisten);

  const noteTabKey = pack === "aufmass" ? "dateien" : "deckblatt";
  const preview: UnterlagenPreviewItem[] = [];
  if (counts.hasAb) {
    preview.push({
      key: "ab",
      tabKey: "ab",
      kind: "ab",
      title: "Auftragsbestätigung",
      subtitle: "ohne Preise",
    });
  }

  for (const sl of packSl) {
    const nr = stuecklisteDisplayNumber(sl);
    const bauteil = String(sl.bauteil || "").trim();
    const isDraft = String(sl.status || "").toLowerCase() !== "fertig";
    preview.push({
      key: `sl-${sl.id}`,
      tabKey: `sl-${sl.id}`,
      kind: "stueckliste",
      title: nr.startsWith("Stückliste") ? nr : `Stückliste ${nr}`,
      subtitle: [bauteil, isDraft ? "Entwurf" : null].filter(Boolean).join(" · ") || null,
    });
  }

  const files = packItems
    .filter((item) => item.entry_type === "file" && !linkedIds.has(item.id))
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  for (const item of files) {
    preview.push({
      key: `file-${item.id}`,
      tabKey: `file-${item.id}`,
      kind: "file",
      title: unterlagenItemTitle(item),
      subtitle: item.filename && item.title ? item.filename : null,
    });
  }

  const notes = packItems
    .filter((item) => item.entry_type === "note")
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  for (const item of notes) {
    preview.push({
      key: `note-${item.id}`,
      tabKey: noteTabKey,
      kind: "note",
      title: unterlagenItemTitle(item),
      subtitle: "Notiz",
    });
  }

  const addresses = packItems
    .filter((item) => item.entry_type === "address")
    .slice()
    .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
  for (const item of addresses) {
    preview.push({
      key: `addr-${item.id}`,
      tabKey: noteTabKey,
      kind: "address",
      title: unterlagenItemTitle(item),
      subtitle: "Adresse",
    });
  }

  const limit = options.limit ?? UNTERLAGEN_PREVIEW_LIMIT;
  return {
    items: preview.slice(0, limit),
    hiddenCount: Math.max(0, preview.length - limit),
    counts,
  };
}

export function unterlagenCountChips(counts: UnterlagenPreviewCounts): string[] {
  const chips: string[] = [];
  if (counts.stuecklisten) {
    chips.push(`${counts.stuecklisten} Stückliste${counts.stuecklisten === 1 ? "" : "n"}`);
  }
  if (counts.files) chips.push(`${counts.files} Datei${counts.files === 1 ? "" : "en"}`);
  if (counts.notes) chips.push(`${counts.notes} Notiz${counts.notes === 1 ? "" : "en"}`);
  if (counts.addresses) {
    chips.push(`${counts.addresses} Adresse${counts.addresses === 1 ? "" : "n"}`);
  }
  if (counts.hasAb) chips.push("AB");
  return chips;
}
